import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Typography,
  Spin,
  Alert,
  Tag,
  Space,
  Button,
  Divider,
  Tooltip,
  message,
  Input,
  Modal,
  Empty,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleOutlined,
  CopyOutlined,
  EditOutlined,
  HistoryOutlined,
  RobotOutlined,
  WarningFilled,
  InfoCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import {
  reviewSingle,
  getResultsForPrompt,
  updateReviewerAction,
  getReviewerActionHistory,
  type PromptKind,
  type PromptReviewResult,
  type PromptReviewScores,
  type ReviewerAction,
  type PromptReviewResultAction,
} from '@/api/promptReview';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import {
  useWorkflowProgress,
  type WorkflowStep,
  type WorkflowProgressState,
} from '@/realtime/useWorkflowProgress';
import { WorkflowProgressView } from '@/realtime/WorkflowProgressView';

// ─────────────────────────────────────────────────────────────────────────────
// PROMPT_REVIEW workflow steps — must match WorkflowNode.id values from the
// [CodeFirstWorkflow]-decorated PromptReviewWorkflowDefaults.BuildDefault.
// If the C# default ever renames a node, change here. The tests' typecheck
// won't catch it (different language), so when authoring the cookie-cutter
// for a new workflow, copy the shape here and keep them paired.
// ─────────────────────────────────────────────────────────────────────────────
const PROMPT_REVIEW_STEPS: WorkflowStep[] = [
  { id: 'prepare-input', label: 'Preparing input', description: 'Loading prompt body, hashing, running pre-filter' },
  { id: 'review', label: 'Running 11-dimension review', description: 'Specialist skills are activating in parallel' },
  { id: 'persist', label: 'Persisting result', description: 'Writing the review envelope to the database' },
  { id: 'notify', label: 'Notifying', description: 'Pushing the completion signal' },
];

const { Title, Text, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// PromptReviewDrawer — "Review with AI" UX
// ─────────────────────────────────────────────────────────────────────────────
//
// Lifecycle
//   1. Operator clicks "Review with AI" on a Prebuilt prompt row.
//   2. Drawer opens in REVIEWING state. We snapshot the latest RunId for
//      this prompt (so we can tell the new result apart from any prior
//      review), fire POST /api/PromptReview/Single, and poll
//      GET /api/PromptReview/Prompt/{kind}/{id}/Results?take=1 every
//      ~2.5s. When a strictly-newer RunId appears, we render it.
//   3. Operator can Accept / Edit / Dismiss the suggestion. Each action
//      writes an immutable audit row in ai.PromptReviewResultAction
//      transactional with the current-state update.
//
// Why polling (not SignalR)
//   The backend's SignalR notify still fires for any subscriber, but the
//   Agent Studio frontend doesn't yet have a hub-connection layer.
//   Polling is V1's pragmatic choice — self-contained, no new infra.
//   A future SignalR client can drop in without changing this drawer's
//   contract: it just makes the polling redundant.
//
// Drawer (not Modal) per project convention: rich content, multi-section,
// keeps the underlying prompt list visible behind the drawer.
// ─────────────────────────────────────────────────────────────────────────────

type ReviewState =
  | { phase: 'idle' }
  | { phase: 'reviewing'; processKey: string; startedAt: number }
  | { phase: 'ready'; result: PromptReviewResult }
  | { phase: 'failed'; message: string };

interface Props {
  open: boolean;
  onClose: () => void;
  promptKind: PromptKind;
  promptId: number;
  /** Free-form label for the prompt being reviewed (shown in the drawer header). */
  promptLabel?: string | null;
  /**
   * Optional. Called after a successful Accept / Edit — i.e. when the
   * underlying source prompt's Instructions just changed. The parent
   * typically uses this to refetch its list so the new body is visible
   * without a manual refresh.
   */
  onApplied?: () => void;
}

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 90_000;

const DIMENSIONS: Array<{ label: string; key: keyof PromptReviewScores }> = [
  { label: 'Clarity', key: 'clarity' },
  { label: 'Grammar', key: 'grammar' },
  { label: 'Conciseness', key: 'conciseness' },
  { label: 'Tone', key: 'tone' },
  { label: 'Specificity', key: 'specificity' },
  { label: 'Efficacy', key: 'efficacy' },
  { label: 'Behavior (not mechanics)', key: 'behavior_not_mechanics' },
  { label: 'Citation', key: 'citation' },
  { label: 'Consistency', key: 'consistency' },
  { label: 'Clinical accuracy', key: 'clinical_accuracy' },
  { label: 'Clinical safety', key: 'clinical_safety' },
];

/** Latest by RunId. Returns null on empty input. */
function latestByRunId(rows: PromptReviewResult[]): PromptReviewResult | null {
  if (!rows.length) return null;
  return rows.reduce((acc, r) => (r.runId > acc.runId ? r : acc), rows[0]);
}

function safeParseScores(json: string | null | undefined): PromptReviewScores | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as PromptReviewScores;
  } catch {
    return null;
  }
}

function scoreColor(score: number | null | undefined): string {
  if (score == null) return colors.slate[500];
  if (score >= 4) return colors.green[600];
  if (score === 3) return colors.amber[600];
  return colors.red[600];
}

function flagToneColor(flag: string): string {
  if (flag.includes('clinical_safety')) return colors.red[600];
  if (flag.includes('clinical_accuracy')) return colors.red[500];
  if (flag.includes('phi_leak') || flag.includes('mental_health')) return colors.red[600];
  if (flag.includes('placeholder') || flag.includes('charset')) return colors.amber[600];
  return colors.slate[500];
}

export const PromptReviewDrawer = observer(function PromptReviewDrawer({
  open,
  onClose,
  promptKind,
  promptId,
  promptLabel,
  onApplied,
}: Props) {
  const { themeStore, authStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [state, setState] = useState<ReviewState>({ phase: 'idle' });
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<PromptReviewResultAction[]>([]);
  const [editedRewrite, setEditedRewrite] = useState('');
  const [actionInFlight, setActionInFlight] = useState<ReviewerAction | null>(null);

  // Live workflow progress over SignalR. The hook subscribes to the
  // /aiprocesshub group "AI-Process-Merchant-{userId}" — the same group
  // the AIProcessManager pushes into after every AddLog inside the
  // workflow. Polling stays in place as a safety net (see useEffect
  // below); whichever source produces the result first wins.
  const userIdNum = (() => {
    const raw = authStore.user?.userId;
    if (raw == null) return null;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
  })();
  const progress = useWorkflowProgress({
    processKey: state.phase === 'reviewing' ? state.processKey : null,
    steps: PROMPT_REVIEW_STEPS,
    userType: 2,           // ConversationUserType.Merchant
    userId: userIdNum,
    enabled: open && state.phase === 'reviewing',
  });

  // Snapshot of the latest RunId at the moment the drawer opens, so we
  // can distinguish "previous review" from "the review we just kicked off."
  const baselineRunIdRef = useRef<number>(0);
  const pollHandleRef = useRef<number | null>(null);

  // ── Open: snapshot baseline + kick off a new review ───────────────────
  useEffect(() => {
    if (!open) {
      // Cleanup on close.
      if (pollHandleRef.current != null) {
        window.clearTimeout(pollHandleRef.current);
        pollHandleRef.current = null;
      }
      setState({ phase: 'idle' });
      setEditOpen(false);
      setHistoryOpen(false);
      setHistory([]);
      setActionInFlight(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Snapshot baseline — everything currently in the table.
        const existing = await getResultsForPrompt(promptKind, promptId, 1);
        baselineRunIdRef.current = existing[0]?.runId ?? 0;

        // Trigger the workflow.
        const trigger = await reviewSingle(promptKind, promptId);
        if (cancelled) return;
        setState({
          phase: 'reviewing',
          processKey: trigger.processKey,
          startedAt: Date.now(),
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to start review';
        setState({ phase: 'failed', message: msg });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, promptKind, promptId]);

  // ── Poll for the new result row ────────────────────────────────────────
  useEffect(() => {
    if (state.phase !== 'reviewing') return;

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const rows = await getResultsForPrompt(promptKind, promptId, 1);
        const latest = latestByRunId(rows);
        if (latest && latest.runId > baselineRunIdRef.current) {
          if (!cancelled) {
            setState({ phase: 'ready', result: latest });
            setEditedRewrite(latest.suggestedRewrite ?? '');
          }
          return;
        }
      } catch {
        // Polling errors are transient; keep trying within the timeout.
      }

      if (Date.now() - state.startedAt > POLL_TIMEOUT_MS) {
        if (!cancelled) {
          setState({
            phase: 'failed',
            message:
              'Review took longer than expected (90 s). The workflow may still be running — ' +
              'check back shortly. The result will appear in the per-prompt review history when it lands.',
          });
        }
        return;
      }

      pollHandleRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
    };

    pollHandleRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (pollHandleRef.current != null) {
        window.clearTimeout(pollHandleRef.current);
        pollHandleRef.current = null;
      }
    };
  }, [state, promptKind, promptId]);

  // ── Reviewer actions ────────────────────────────────────────────────────
  const submitAction = async (
    resultId: number,
    action: ReviewerAction,
    note?: string,
  ) => {
    setActionInFlight(action);
    try {
      await updateReviewerAction(resultId, action, note);
      message.success(
        action === 'Accepted' ? 'Rewrite applied — prompt updated.'
        : action === 'Edited' ? 'Edited rewrite applied — prompt updated.'
        : `Marked as ${action}`,
      );
      setState((prev) =>
        prev.phase === 'ready'
          ? {
              phase: 'ready',
              result: {
                ...prev.result,
                reviewerAction: action,
                reviewerNote: note ?? prev.result.reviewerNote,
              },
            }
          : prev,
      );
      // Accept and Edit mutate the source prompt's Instructions on the
      // backend. Tell the parent to refetch its list so the new body is
      // visible without a manual reload, and close the drawer so the
      // operator returns to the (now-updated) list.
      if (action === 'Accepted' || action === 'Edited') {
        onApplied?.();
        onClose();
      }
    } catch (err) {
      // Backend may return a "stale review" error — the source body
      // changed since the review. Surface that clearly so the operator
      // knows to re-trigger Review with AI rather than retry blindly.
      const msg = err instanceof Error ? err.message : 'Failed to save action';
      if (/stale review/i.test(msg)) {
        message.error({
          content:
            'This review is stale — the prompt body changed since the review ran. ' +
            'Close this drawer and click "Review with AI" again.',
          duration: 8,
        });
      } else {
        message.error(msg);
      }
    } finally {
      setActionInFlight(null);
    }
  };

  const onShowHistory = async (resultId: number) => {
    try {
      const rows = await getReviewerActionHistory(resultId);
      setHistory(rows);
      setHistoryOpen(true);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to load history');
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <RobotOutlined aria-hidden />
          <span>Review with AI</span>
          {promptLabel ? (
            <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
              · {promptLabel}
            </Text>
          ) : null}
        </Space>
      }
      placement="right"
      width={1080}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {/* Live region for screen readers — announces phase transitions. */}
      <div
        role="status"
        aria-live="polite"
        style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}
      >
        {state.phase === 'reviewing' && 'Reviewing your prompt. Typical wait is 25 to 40 seconds.'}
        {state.phase === 'ready' &&
          (state.result.needsReview
            ? `Review complete. The prompt needs review. Aggregate score ${state.result.aggregateScore.toFixed(2)} out of 5.`
            : `Review complete. No issues found. Aggregate score ${state.result.aggregateScore.toFixed(2)} out of 5.`)}
        {state.phase === 'failed' && `Review failed: ${state.message}`}
      </div>

      {state.phase === 'reviewing' && (
        <ReviewingView
          startedAt={state.startedAt}
          isDark={isDark}
          progress={progress}
        />
      )}
      {state.phase === 'failed' && <FailedView message={state.message} />}
      {state.phase === 'ready' && (
        <ReadyView
          result={state.result}
          isDark={isDark}
          editOpen={editOpen}
          editedRewrite={editedRewrite}
          actionInFlight={actionInFlight}
          onSetEditedRewrite={setEditedRewrite}
          onOpenEdit={() => setEditOpen(true)}
          onCloseEdit={() => setEditOpen(false)}
          onAccept={() => {
            const r = state.result;
            const oldLen = r.originalBody?.length ?? 0;
            const newLen = r.suggestedRewrite?.length ?? 0;
            Modal.confirm({
              title: 'Apply the AI rewrite to this prompt?',
              width: 540,
              content: (
                <div>
                  <p style={{ marginTop: 0, marginBottom: 8 }}>
                    The current prompt body will be <Text strong>replaced</Text> with
                    the AI's suggested rewrite. This change is recorded in the
                    audit history (you can review it on the prompt later).
                  </p>
                  <p style={{ margin: 0, fontSize: 12 }}>
                    <Text type="secondary">
                      Body length: {oldLen} → {newLen} chars
                      {' · '}
                      RunId #{r.runId}
                      {r.aggregateScore != null
                        ? ` · score ${r.aggregateScore.toFixed(2)}/5`
                        : ''}
                    </Text>
                  </p>
                </div>
              ),
              okText: 'Replace prompt',
              okButtonProps: { type: 'primary' },
              cancelText: 'Cancel',
              onOk: () => submitAction(r.id, 'Accepted'),
            });
          }}
          onDismiss={() =>
            Modal.confirm({
              title: 'Dismiss this review?',
              content:
                'The result will remain in history; the suggested rewrite will not be applied.',
              okText: 'Dismiss',
              onOk: () => submitAction(state.result.id, 'Dismissed'),
            })
          }
          onSaveEdit={async (note) => {
            await submitAction(state.result.id, 'Edited', note);
            setEditOpen(false);
          }}
          onShowHistory={() => onShowHistory(state.result.id)}
        />
      )}

      <HistoryModal
        open={historyOpen}
        history={history}
        isDark={isDark}
        onClose={() => setHistoryOpen(false)}
      />
    </Drawer>
  );
});

// ── Subviews ─────────────────────────────────────────────────────────────────

function ReviewingView({
  startedAt,
  isDark,
  progress,
}: {
  startedAt: number;
  isDark: boolean;
  progress: WorkflowProgressState;
}) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 500);
    return () => window.clearInterval(id);
  }, [startedAt]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          borderRadius: 8,
          background: isDark ? colors.slate[800] : colors.ink[50],
          border: `1px solid ${isDark ? colors.slate[700] : colors.ink[100]}`,
        }}
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Reviewing your prompt…
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            11 dimensions, panel of specialist skills · typical run 25–40 s
          </Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Spin size="small" />
          <div>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
              Elapsed {elapsed}s
            </Text>
          </div>
        </div>
      </div>

      <WorkflowProgressView state={progress} steps={PROMPT_REVIEW_STEPS} isDark={isDark} />

      <div>
        <Text strong style={{ fontSize: 13 }}>
          What's being scored
        </Text>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 6,
            marginTop: 8,
          }}
        >
          {DIMENSIONS.map((d) => (
            <div
              key={d.key}
              style={{
                padding: '6px 10px',
                borderRadius: 4,
                border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                color: isDark ? colors.slate[400] : colors.slate[500],
                fontSize: 12,
              }}
            >
              {d.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FailedView({ message }: { message: string }) {
  return (
    <Alert
      type="warning"
      showIcon
      message="Review didn't complete"
      description={message}
      style={{ marginTop: 12 }}
    />
  );
}

interface ReadyProps {
  result: PromptReviewResult;
  isDark: boolean;
  editOpen: boolean;
  editedRewrite: string;
  actionInFlight: ReviewerAction | null;
  onSetEditedRewrite: (v: string) => void;
  onOpenEdit: () => void;
  onCloseEdit: () => void;
  onAccept: () => void;
  onDismiss: () => void;
  onSaveEdit: (note: string) => void;
  onShowHistory: () => void;
}

function ReadyView({
  result,
  isDark,
  editOpen,
  editedRewrite,
  actionInFlight,
  onSetEditedRewrite,
  onOpenEdit,
  onCloseEdit,
  onAccept,
  onDismiss,
  onSaveEdit,
  onShowHistory,
}: ReadyProps) {
  const scores = useMemo(() => safeParseScores(result.scores_Json), [result.scores_Json]);
  const flags = (result.autoFlags ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ResultBanner result={result} isDark={isDark} />

      {/* Auto-flags */}
      {flags.length > 0 && (
        <div>
          <Text strong>Flags</Text>
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {flags.map((f) => (
              <Tag
                key={f}
                color="default"
                style={{ background: flagToneColor(f), color: 'white', borderColor: 'transparent' }}
              >
                {f}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Rationale */}
      {result.rationale && (
        <div>
          <Text strong>Rationale</Text>
          <Paragraph style={{ marginTop: 6, marginBottom: 0 }}>{result.rationale}</Paragraph>
        </div>
      )}

      {/* Per-dimension scores */}
      <div>
        <Text strong>Per-dimension scores</Text>
        {scores ? (
          <ScoreGrid scores={scores} isDark={isDark} />
        ) : (
          <Empty
            description={
              <Text type="secondary">
                Score breakdown unavailable — the synthesizer's envelope didn't include parseable
                scores. Aggregate score is still shown above.
              </Text>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ marginTop: 12 }}
          />
        )}
      </div>

      {/* Original vs Suggested rewrite — side-by-side comparison */}
      <RewriteComparison
        original={result.originalBody ?? ''}
        suggested={result.suggestedRewrite ?? null}
        isDark={isDark}
      />

      <Divider style={{ margin: '8px 0' }} />

      {/* Reviewer actions */}
      <Space wrap>
        <Tooltip title="Accept the AI's suggested rewrite. Records your decision in the audit trail.">
          <Button
            type="primary"
            icon={<CheckCircleFilled />}
            onClick={onAccept}
            disabled={result.reviewerAction === 'Accepted' || actionInFlight != null}
            loading={actionInFlight === 'Accepted'}
          >
            Accept
          </Button>
        </Tooltip>
        <Tooltip title="Edit the rewrite before recording the decision.">
          <Button
            icon={<EditOutlined />}
            onClick={onOpenEdit}
            disabled={actionInFlight != null}
          >
            Edit
          </Button>
        </Tooltip>
        <Tooltip title="Dismiss this review — keep the original prompt as-is.">
          <Button
            danger
            icon={<CloseCircleFilled />}
            onClick={onDismiss}
            disabled={result.reviewerAction === 'Dismissed' || actionInFlight != null}
            loading={actionInFlight === 'Dismissed'}
          >
            Dismiss
          </Button>
        </Tooltip>
        <Tooltip title="Show every action transition for this review (audit trail).">
          <Button
            type="text"
            icon={<HistoryOutlined />}
            onClick={onShowHistory}
            disabled={actionInFlight != null}
          >
            History
          </Button>
        </Tooltip>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Current state: <Tag>{result.reviewerAction}</Tag>
        </Text>
      </Space>

      {/* Edit modal */}
      <Modal
        title="Edit the suggested rewrite"
        open={editOpen}
        onCancel={onCloseEdit}
        onOk={() => onSaveEdit(editedRewrite)}
        okText="Save edited rewrite"
        confirmLoading={actionInFlight === 'Edited'}
        width={720}
        destroyOnClose
      >
        <Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 12 }}>
          <InfoCircleOutlined /> Your edits replace the AI's rewrite in the audit trail. The
          original suggestion is preserved in history.
        </Paragraph>
        <Input.TextArea
          value={editedRewrite}
          onChange={(e) => onSetEditedRewrite(e.target.value)}
          rows={12}
          placeholder="Edit the AI's suggested rewrite here…"
          aria-label="Edited rewrite"
        />
      </Modal>
    </div>
  );
}

function ResultBanner({ result, isDark }: { result: PromptReviewResult; isDark: boolean }) {
  const isWarn = result.needsReview;
  const tone = isWarn ? colors.amber[600] : colors.green[600];
  const Icon = isWarn ? WarningFilled : CheckCircleFilled;
  const title = isWarn ? 'This prompt needs review' : 'No issues found';
  const bg = isWarn
    ? isDark
      ? 'rgba(217, 119, 6, 0.12)'
      : colors.amber[100]
    : isDark
      ? 'rgba(5, 150, 105, 0.12)'
      : colors.green[100];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 16px',
        borderRadius: 8,
        border: `1px solid ${tone}`,
        background: bg,
      }}
    >
      <Icon style={{ fontSize: 24, color: tone }} aria-hidden />
      <div style={{ flex: 1 }}>
        <Title level={5} style={{ margin: 0, color: isDark ? colors.slate[100] : undefined }}>
          {title}
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Aggregate score:{' '}
          <Text strong style={{ color: scoreColor(Math.floor(result.aggregateScore)) }}>
            {result.aggregateScore.toFixed(2)}
          </Text>
          {' / 5  ·  Run #' + result.runId}
        </Text>
      </div>
    </div>
  );
}

function ScoreGrid({ scores, isDark }: { scores: PromptReviewScores; isDark: boolean }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 6,
        marginTop: 6,
      }}
    >
      {DIMENSIONS.map((r) => {
        const v = scores[r.key];
        return (
          <div
            key={r.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 10px',
              borderRadius: 4,
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              background: isDark ? colors.slate[800] : 'transparent',
            }}
          >
            <Text style={{ fontSize: 13, color: isDark ? colors.slate[200] : undefined }}>
              {r.label}
            </Text>
            {v == null ? (
              <Text type="secondary" style={{ fontSize: 12 }}>n/a</Text>
            ) : (
              <Text strong style={{ color: scoreColor(v) }}>
                {v}/5
              </Text>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RewriteComparison — original vs. suggested rewrite, side-by-side
// ─────────────────────────────────────────────────────────────────────────────
//
// At the wider drawer width (1080px) the two panes sit comfortably side by
// side with ~480px each. On a narrower viewport the CSS grid auto-fit drops
// to a single column with the panes stacked.
//
// Edge cases handled:
//   • No suggested rewrite (every applicable dimension scored ≥ 4) → render
//     only the original pane and a small "no rewrite needed" note next to it.
//   • Long bodies → both panes scroll vertically (max-height); the headers
//     stay in place so the user keeps the labels in view while reading.
//   • Copy buttons on each pane so the operator can paste either body into
//     the source-of-truth editor (Prebuilt Prompts page, in our case).
// ─────────────────────────────────────────────────────────────────────────────

function RewriteComparison({
  original,
  suggested,
  isDark,
}: {
  original: string;
  suggested: string | null;
  isDark: boolean;
}) {
  const noRewrite = suggested == null || suggested.trim().length === 0;

  return (
    <div>
      <Space style={{ marginBottom: 6 }}>
        <Text strong>Prompt comparison</Text>
        {!noRewrite && (
          <Tag
            color="default"
            style={{
              background: 'transparent',
              borderColor: isDark ? colors.slate[600] : colors.slate[300],
              color: isDark ? colors.slate[300] : colors.slate[600],
              fontSize: 11,
            }}
          >
            <ArrowRightOutlined aria-hidden /> rewrite suggested
          </Tag>
        )}
        {noRewrite && (
          <Tag color="default" style={{ background: 'transparent' }}>
            no rewrite needed
          </Tag>
        )}
      </Space>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: noRewrite ? '1fr' : 'repeat(auto-fit, minmax(380px, 1fr))',
          gap: 12,
        }}
      >
        <RewritePane
          label="Original"
          body={original}
          tone="neutral"
          isDark={isDark}
        />
        {!noRewrite && (
          <RewritePane
            label="Suggested rewrite"
            body={suggested ?? ''}
            tone="suggested"
            isDark={isDark}
          />
        )}
      </div>

      {noRewrite && (
        <Paragraph
          type="secondary"
          style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}
        >
          The synthesizer did not propose a rewrite — every applicable dimension
          scored ≥ 4. The original prompt above is shown for reference.
        </Paragraph>
      )}
    </div>
  );
}

function RewritePane({
  label,
  body,
  tone,
  isDark,
}: {
  label: string;
  body: string;
  tone: 'neutral' | 'suggested';
  isDark: boolean;
}) {
  // Subtle accent on the suggested pane so the eye locates "what changed"
  // without a heavy diff highlight. The diff treatment can land later as a
  // toggle ("Diff view") if word-level highlighting becomes worth the cost.
  const accent =
    tone === 'suggested'
      ? isDark
        ? colors.violet[500]
        : colors.violet[500]
      : isDark
        ? colors.slate[600]
        : colors.slate[300];
  const bg =
    tone === 'suggested'
      ? isDark
        ? 'rgba(139, 92, 246, 0.08)'
        : colors.violet[50]
      : isDark
        ? colors.slate[800]
        : colors.slate[50];

  const onCopy = async () => {
    try {
      await navigator.clipboard?.writeText(body ?? '');
      message.success(`${label} copied`);
    } catch {
      message.error('Copy failed');
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 6,
        border: `1px solid ${accent}`,
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          borderBottom: `1px solid ${accent}`,
          background:
            tone === 'suggested'
              ? isDark
                ? 'rgba(139, 92, 246, 0.16)'
                : colors.violet[100]
              : isDark
                ? colors.slate[700]
                : colors.slate[100],
        }}
      >
        <Text
          strong
          style={{
            fontSize: 12,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: isDark ? colors.slate[200] : colors.slate[700],
          }}
        >
          {label}
        </Text>
        <Tooltip title={`Copy ${label.toLowerCase()} to clipboard`}>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={onCopy}
            aria-label={`Copy ${label}`}
          />
        </Tooltip>
      </div>
      <Paragraph
        style={{
          whiteSpace: 'pre-wrap',
          padding: 12,
          margin: 0,
          maxHeight: 360,
          overflowY: 'auto',
          color: isDark ? colors.slate[100] : colors.slate[900],
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        {body || <Text type="secondary" italic>(empty)</Text>}
      </Paragraph>
    </div>
  );
}

function HistoryModal({
  open,
  onClose,
  history,
  isDark,
}: {
  open: boolean;
  onClose: () => void;
  history: PromptReviewResultAction[];
  isDark: boolean;
}) {
  return (
    <Modal
      title="Reviewer action history"
      open={open}
      onCancel={onClose}
      footer={null}
      width={620}
      destroyOnClose
    >
      {history.length === 0 ? (
        <Empty
          description={<Text type="secondary">No actions recorded yet.</Text>}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {history.map((h) => (
            <div
              key={h.id}
              style={{
                padding: '8px 12px',
                borderRadius: 6,
                border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                background: isDark ? colors.slate[800] : 'transparent',
              }}
            >
              <Space wrap>
                <Tag color="default">{(h.previousAction ?? '—') + ' → ' + h.action}</Tag>
                <ClockCircleOutlined aria-hidden />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {new Date(h.actionAtUtc).toLocaleString()}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  by user #{h.actionBy}
                </Text>
              </Space>
              {h.note && (
                <Paragraph
                  style={{
                    margin: '4px 0 0 0',
                    fontSize: 13,
                    color: isDark ? colors.slate[200] : undefined,
                  }}
                >
                  {h.note}
                </Paragraph>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
